// Use direct backend URL in development (bypasses Vite proxy)
// In production, uses relative URLs (empty string) since frontend is served by backend
const BACKEND_URI = import.meta.env.VITE_API_BASE_URL || "";

import {
    ChatAppResponse,
    ChatAppResponseOrError,
    ChatAppRequest,
    Config,
    SimpleAPIResponse,
    UserFilesResponse,
    ChatHistory,
    UserProfiles,
    Projects,
    ProjectsApiResponse,
    CreateProjectResponse,
    WorkProductRequest,
    WorkProductQuestion,
    VectorFile,
    UserProfileSettings,
    SubscriptionStatus,
    ProjectRebuildHierarchy
} from "./models";

import fetchWithAuth from "@/api/secureFetch";
// Re-export work processor functions for convenience
export * from "./workProcessor";
import { getWorkProcessorUrl } from "./workProcessor";
import authenticatedFetch from "@/api/authenticatedFetch";
import { WorkProductOpenAIPrompt } from "./models/workproduct_openai_prompts";
import { WorkProductActiveList, WorkProductsApiResponse, WorkProductsResponse, WorkProductDB } from "./models/workproduct";
import { WorkProductSection, WorkProductSectionpiResponse } from "./models/workproduct_sections";
import { WorkProductGroup } from "./models/workproduct_group";

//import { useLogin, appServicesToken } from "../authConfig";
import { WorkProductQuestionAnswers } from "@/api/models/workproduct_questions";

export function getHeaders(idToken: string | undefined): Record<string, string> {
    if (idToken) {
        return { Authorization: `Bearer ${idToken}` };
    }
    return {};
}

export async function configApi(): Promise<Config> {
    const response = await fetch(`${BACKEND_URI}/config`, {
        method: "GET"
    });
    return (await response.json()) as Config;
}

export async function askApi(request: ChatAppRequest, idToken: string | undefined): Promise<ChatAppResponse> {
    const response = await fetch(`${BACKEND_URI}/ask`, {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    const parsedResponse: ChatAppResponseOrError = await response.json();
    if (response.status > 299 || !response.ok) {
        throw Error(parsedResponse.error || "Unknown error");
    }

    return parsedResponse as ChatAppResponse;
}

export async function aiWorkProductApi(request: WorkProductRequest, idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/ai_work_product`, {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
}

export async function chatShadowApi(request: ChatAppRequest, idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat-shadow`, {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });
}

export async function getChatHistoryApi(idToken: string | undefined): Promise<ChatHistory> {
    const response = await fetch(`${BACKEND_URI}/chat/history`, {
        method: "GET",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" }
    });

    if (!response.ok) {
        throw new Error(`Getting chat history failed: ${response.statusText}`);
    }

    const chatHistory: ChatHistory = await response.json();

    return chatHistory as ChatHistory;
}

/**
 * Marks work products as needing rebuild when their source data changes.
 * This is triggered when files are added/removed from projects or features,
 * indicating that AI-generated work products may be based on stale data.
 *
 * @param projectIds - Array of project IDs, or null for global updates (all projects)
 * @param featureIds - Array of feature IDs, or null for project-level updates
 * @param userStoryIds - Array of user story UUIDs, or null
 * @param sourceWarehouseId - The source warehouse ID, or null
 * @param fileUpdate - True if this is a file update operation (defaults to true)
 * @param idToken - The authentication token
 */
export async function markWorkProductsNeedingRebuild(
    projectIds: number[] | null,
    featureIds: number[] | null,
    userStoryIds: string[] | null,
    sourceWarehouseId: number | null,
    fileUpdate: boolean,
    idToken: string | undefined
): Promise<Response> {
    const response = await fetchWithAuth(`/api/workproduct/mark_workproducts_for_update`, idToken ?? "", {
        method: "POST",
        body: JSON.stringify({
            p_project_ids: projectIds,
            p_feature_ids: featureIds,
            p_user_story_ids: userStoryIds,
            p_source_warehouseid: sourceWarehouseId,
            p_file_update: fileUpdate
        })
    });
    if (!response.ok) {
        throw new Error(`Marking work products for rebuild failed: ${response.statusText}`);
    }
    const dataResponse: Response = await response.json();
    return dataResponse;
}

export async function saveChatHistoryApi(history: [user: string, response: ChatAppResponse][][], idToken: string | undefined): Promise<Response> {
    return await fetch(`${BACKEND_URI}/chat/history`, {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify({ chatHistory: history })
    });
}

export async function uploadUserRegularFileApi(request: FormData, idToken: string): Promise<SimpleAPIResponse> {
    const response = await fetch(`${BACKEND_URI}/api/user/files/upload`, {
        method: "POST",
        headers: getHeaders(idToken),
        body: request
    });

    if (!response.ok) {
        throw new Error(`Uploading files failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function deleteUserFileApi(filename: string, idToken: string): Promise<SimpleAPIResponse> {
    const response = await fetch(`${BACKEND_URI}/api/user/files/delete`, {
        method: "POST",
        headers: { ...getHeaders(idToken), "Content-Type": "application/json" },
        body: JSON.stringify({ filename })
    });

    if (!response.ok) {
        throw new Error(`Deleting file failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function listUserFilesApi(idToken: string): Promise<UserFilesResponse> {
    const response = await fetch(`${BACKEND_URI}/api/user/files/list`, {
        method: "GET",
        headers: getHeaders(idToken)
    });

    if (!response.ok) {
        throw new Error(`Listing files failed: ${response.statusText}`);
    }
    return await response.json();
}

export async function updateUserProfileApi(request: Record<string, any>, idToken: string): Promise<{ user: UserProfiles; project: Projects }> {
    const response = await authenticatedFetch(`${BACKEND_URI}/api/user/update`, idToken, {
        method: "PUT",
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        throw new Error(`Creating profile failed: ${response.statusText}`);
    }

    const responseData = await response.json();
    return responseData;
}

export async function updateUserProfileSettingsApi(userid: number, request: UserProfileSettings, idToken: string): Promise<UserProfiles> {
    const response = await authenticatedFetch(`${BACKEND_URI}/api/user/profile/settings/${userid}`, idToken, {
        method: "PUT",
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        throw new Error(`Updating profile settings failed: ${response.statusText}`);
    }

    const profileData: UserProfiles = await response.json();
    return profileData;
}

//TODO: Fix this if you have a token you have already pulled from the clerkID and can use the UserID
export async function fetchUserProfile(clerkId: string, token: string): Promise<UserProfiles> {
    const apiUrl = `${BACKEND_URI}/api/user/profile?clerk_id=${clerkId}`;

    const response = await authenticatedFetch(apiUrl, token, {
        method: "GET"
    });

    if (!response.ok) {
        throw new Error(`Fetching profile failed: ${response.statusText}`);
    }

    const profileData: UserProfiles = await response.json();
    return profileData;
}

export async function listUserProjectsApi(idToken: string): Promise<ProjectsApiResponse> {
    const response = await fetch(`${BACKEND_URI}/api/user/projects/list`, {
        method: "GET",
        headers: {
            ...getHeaders(idToken)
        }
    });

    if (!response.ok) {
        throw new Error(`Listing projects failed: ${response.statusText}`);
    }

    return await response.json();
}

export async function createProjectApi(request: Record<string, any>, idToken: string): Promise<CreateProjectResponse> {
    const response = await fetch(`${BACKEND_URI}/api/project`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    return await response.json();
}

import { Feature } from "./models/feature";

export async function updateProjectApi(request: Record<string, any>, idToken: string): Promise<Projects> {
    // Create form data to match backend expectations
    const formData = new FormData();
    formData.append("projectname", request.project_name || "");
    formData.append("projectdesc", request.project_description || "");
    formData.append("status", request.status || "Active");
    if (request.current !== undefined) {
        formData.append("current", request.current.toString());
    }
    if (request.history !== undefined) {
        formData.append("history", JSON.stringify(request.history));
    }
    if (request.external_link_type !== undefined) {
        formData.append("external_link_type", request.external_link_type);
    }
    if (request.external_link_key !== undefined) {
        formData.append("external_link_key", request.external_link_key);
    }

    const response = await fetch(`${BACKEND_URI}/api/project/${request.project_id}`, {
        method: "PUT",
        headers: {
            Authorization: `Bearer ${idToken}`
        },
        body: formData
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    // Extract and transform the project data to match the `Projects` interface
    const { project } = await response.json();
    return {
        project_id: project.project_id, // Map `project_id` from backend
        project_name: project.project_name, // Map `project_name` from backend
        project_description: project.project_description, // Map `project_description` from backend
        status: project.status,
        created_date: project.created_date,
        last_updated: project.last_updated,
        current: project.current,
        history: project.history,
        external_link_type: project.external_link_type,
        external_link_key: project.external_link_key
    };
}

// Feature API functions
export async function fetchProjectFeaturesApi(projectId: number, idToken: string): Promise<Feature[]> {
    const response = await fetch(`${BACKEND_URI}/api/projects/${projectId}/features`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { features } = await response.json();
    return features;
}

export async function createFeatureApi(featureData: Record<string, any>, idToken: string): Promise<Feature> {
    const response = await fetch(`${BACKEND_URI}/api/features`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify(featureData)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { feature } = await response.json();
    return feature;
}

export async function updateFeatureApi(featureId: number, featureData: Record<string, any>, idToken: string): Promise<Feature> {
    const response = await fetch(`${BACKEND_URI}/api/features/${featureId}`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify(featureData)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { feature } = await response.json();
    return feature;
}

export async function getFeatureApi(featureId: number | string, idToken: string): Promise<Feature> {
    const response = await fetch(`${BACKEND_URI}/api/features/${featureId}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { feature } = await response.json();
    return feature;
}

export async function deleteFeatureApi(featureId: number, idToken: string): Promise<void> {
    const response = await fetch(`${BACKEND_URI}/api/features/${featureId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }
}

export async function fetchProjectByIdAPI(projectId: string, idToken: string): Promise<Projects> {
    const response = await fetch(`${BACKEND_URI}/api/user/project/${projectId}`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Fetching project failed: ${response.statusText}`);
    }

    return await response.json();
}

export async function setCurrentProjectApi(projectId: number, idToken: string): Promise<Projects> {
    const response = await fetch(`${BACKEND_URI}/api/project/${projectId}/current`, {
        method: "PUT",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Setting project as current failed: ${response.statusText}`);
    }

    const data = await response.json();
    // The API returns { project: {...} }, so extract the project
    return data.project || data;
}

export async function getProjectRebuildHierarchyApi(projectId: number, idToken: string): Promise<ProjectRebuildHierarchy> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/project/${projectId}/rebuild-hierarchy`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        throw new Error(`Fetching project rebuild hierarchy failed: ${response.statusText}`);
    }

    return await response.json();
}

export interface ProjectFeatureHierarchy {
    project_id: number;
    features: Array<{
        featureid?: number;
        id?: number;
        title?: string;
        name?: string;
        user_stories?: Array<{
            user_story_id?: string;
            id?: string;
            story_number?: string;
            title?: string;
            [key: string]: unknown;
        }>;
        [key: string]: unknown;
    }>;
}

export async function getProjectFeatureHierarchyApi(projectId: number, idToken: string): Promise<ProjectFeatureHierarchy> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/project/${projectId}/feature-hierarchy`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        throw new Error(`Fetching project feature hierarchy failed: ${response.statusText}`);
    }

    return await response.json();
}

export async function deleteProjectApi(projectId: number, idToken: string): Promise<{ message: string }> {
    const response = await fetch(`${BACKEND_URI}/api/project/${projectId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(`Failed to delete project: ${errorMessage}`);
    }

    return await response.json();
}

export async function signUpUserApi(request: Record<string, any>): Promise<SimpleAPIResponse> {
    const response = await fetch(`${BACKEND_URI}/api/user/signUp`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        throw new Error(`Creating profile failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

// User Answers
export async function addUserAnswersApi(request: Record<string, any>, idToken: string) {
    console.log("Adding user answers with request:", request); // Debugging line

    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/answers`, idToken, {
        method: "POST",
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        throw new Error(`Adding Introduction failed: ${response.statusText}`);
    }

    const dataResponse: SimpleAPIResponse = await response.json();
    return dataResponse;
}

export async function getUserAnswersApi(projectid: number, idToken: string): Promise<WorkProductQuestionAnswers> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/project/${projectid}/answers`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        throw new Error(`Adding Introduction failed: ${response.statusText}`);
    }

    const dataResponse: WorkProductQuestionAnswers = await response.json();

    if (!dataResponse) {
        throw new Error("No answers found for the specified project.");
    }

    return dataResponse;
}

export async function getUserAnswersWithQuestions(projectid: number, idToken: string): Promise<WorkProductQuestionAnswers[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/project/${projectid}/answerswithquestions`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        throw new Error(`Getting answers failed: ${response.statusText}`);
    }

    const dataResponse: { answers: WorkProductQuestionAnswers[] } = await response.json();

    if (!dataResponse || !dataResponse.answers) {
        throw new Error("No answers found for the specified project.");
    }

    return dataResponse.answers;
}

export async function getAllQuestions(idToken: string): Promise<WorkProductQuestion[]> {
    const response = await fetch(`${BACKEND_URI}/api/workproduct/questions`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });
    if (!response.ok) {
        throw new Error(`Getting all questions failed: ${response.statusText}`);
    }
    const dataResponse: WorkProductQuestion[] = await response.json();
    return dataResponse;
}

export async function getSectionPrompts(sectionid: number, idToken: string): Promise<WorkProductOpenAIPrompt[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/section/${sectionid}/prompts`, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        throw new Error(`Getting section prompts: ${response.statusText}`);
    }
    const dataResponse: WorkProductOpenAIPrompt[] = await response.json();
    return dataResponse;
}

export async function getGuidancePrompts(workproductid: number, idToken: string): Promise<WorkProductOpenAIPrompt> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/workproduct/${workproductid}/prompts`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        throw new Error(`Getting prescriptive guidance failes: ${response.statusText}`);
    }
    const dataResponse: WorkProductOpenAIPrompt = await response.json();
    return dataResponse;
}

export async function adminPromptSave(input_json: any, idToken: string): Promise<boolean> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/prompts/admin/save`, idToken, {
        method: "POST",
        body: JSON.stringify(input_json)
    });

    if (!response.ok) {
        throw new Error(`Admin Prompt Save Failed: ${response.statusText}`);
    }
    const dataResponse: boolean = await response.json();
    return dataResponse;
}

export async function adminPromptRollback(prompt_id: number, idToken: string): Promise<boolean> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/prompts/admin/${prompt_id}/rollback`, idToken, {
        method: "POST"
    });

    if (!response.ok) {
        throw new Error(`Admin Prompt Rollback Failed: ${response.statusText}`);
    }
    const dataResponse: boolean = await response.json();
    return dataResponse;
}

export async function adminPromptList(project_id: number, workproduct_id: number, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/prompts/admin/${project_id}/${workproduct_id}`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        throw new Error(`Admin Prompt List Failed: ${response.statusText}`);
    }
    const dataResponse: any = await response.json();
    return dataResponse;
}

export async function getWorkproductsHierarchy(idToken: string): Promise<WorkProductsApiResponse[]> {
    console.log("Fetching workproducts hierarchy with token:", idToken); // Debugging line

    const response = await fetch(`${BACKEND_URI}/api/workproduct/hierarchy`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        throw new Error(`Listing workproducts hierarchy failed: ${response.statusText}`);
    }

    return await response.json();
}

export async function exportDocument(request: any, idToken: string): Promise<Blob> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/export/document`, idToken, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        const errorText = await response.text();
        console.error("Export failed:", errorText);
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
    }

    return await response.blob();
}

export async function getWorkproducts(request: { projectid: number }, idToken: string): Promise<WorkProductsResponse[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/getworkproducts`, idToken, {
        method: "POST",
        body: JSON.stringify(request)
    });
    if (!response.ok) {
        throw new Error(`Listing workproducts failed: ${response.statusText}`);
    }
    return await response.json();
}

export async function getWorkproductsActiveList(request: { userid: number }, idToken: string): Promise<WorkProductActiveList[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/activelist/${request.userid}`, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        throw new Error(`Workproducts Active List Failed: ${response.statusText}`);
    }
    return await response.json();
}

export async function getWorkProductsByPhase(
    idToken: string,
    projectId: number,
    featureId?: number,
    userStoryId?: string,
    level?: "PRODUCT" | "FEATURE" | "USERSTORY"
): Promise<Record<string, WorkProductDB[]>> {
    let url = `${BACKEND_URI}/api/workproducts/activeworkproductlistings?project_id=${projectId}`;

    if (featureId !== undefined) {
        url += `&feature_id=${featureId}`;
    }

    if (userStoryId) {
        url += `&user_story_id=${userStoryId}`;
    }

    if (level) {
        url += `&level=${level}`;
    }

    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        throw new Error(`Fetching work products by phase failed: ${response.statusText}`);
    }
    // The backend returns work products grouped by phase
    // Each phase contains an array of WorkProductDB objects matching the database structure
    return await response.json();
}

export type SddProgressStatus = "accepted" | "in_progress" | "to_do";

export interface SddProgressCounts {
    total: number;
    accepted: number;
    in_progress: number;
    to_do: number;
    percent: number;
}

export interface SddStageProgress extends SddProgressCounts {
    key: "specify" | "plan" | "task_breakdown" | "implement" | "validate";
    title: string;
    order: number;
}

export interface SddProgressRow {
    level: "PRODUCT" | "FEATURE" | "USERSTORY";
    product_name: string;
    feature_id: number | null;
    feature_name: string | null;
    story_id: string | null;
    story_number: string | null;
    story_name: string | null;
    stage: SddStageProgress["key"];
    work_product: string;
    canonical_variable: string;
    status: SddProgressStatus;
    available: boolean;
}

export interface SddProgressReportRow extends SddProgressRow {
    hierarchy_label: "Product" | "Feature" | "Story";
    story_display_name: string | null;
    stage_label: string;
    status_label: string;
    work_product_label: string;
}

export interface SddProgressReportFilters {
    hierarchy_level?: SddProgressRow["level"];
    stage?: SddStageProgress["key"];
    status?: SddProgressStatus;
}

export interface SddProgressReportPage {
    project_id: number;
    product_name: string | null;
    status_counts: SddProgressCounts;
    rows: SddProgressReportRow[];
    pagination: {
        page: number;
        page_size: number;
        total_pages: number;
        filtered_rows: number;
        overall_rows: number;
        from_row: number;
        to_row: number;
    };
    filters: SddProgressReportFilters;
}

export interface SddProgressSummary {
    totals: SddProgressCounts;
    stages: SddStageProgress[];
}

export interface SddScopeProgress extends SddProgressSummary {
    level: "PRODUCT" | "FEATURE" | "USERSTORY";
    product_name?: string | null;
    feature_id: number | null;
    feature_name: string | null;
    story_id: string | null;
    story_number: string | null;
    story_name: string | null;
}

export interface SddFeatureScopeProgress extends SddScopeProgress {
    level: "FEATURE";
    stories: SddScopeProgress[];
}

export interface SddProgressReport {
    project_id: number;
    product_name: string | null;
    totals: SddProgressCounts;
    stages: SddStageProgress[];
    scope_progress: {
        empty: SddProgressSummary;
        product: SddScopeProgress;
        features: SddFeatureScopeProgress[];
    };
    rows: SddProgressRow[];
}

export async function getSddProgress(idToken: string, projectId: number): Promise<SddProgressReport> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproducts/sdd-progress?project_id=${projectId}`, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        throw new Error(`Fetching SDD progress failed: ${response.statusText}`);
    }
    return await response.json();
}

function sddProgressReportParams(projectId: number, filters: SddProgressReportFilters = {}) {
    const params = new URLSearchParams({ project_id: String(projectId) });
    if (filters.hierarchy_level) params.set("hierarchy_level", filters.hierarchy_level);
    if (filters.stage) params.set("stage", filters.stage);
    if (filters.status) params.set("status", filters.status);
    return params;
}

export async function getSddProgressReportPage(
    idToken: string,
    projectId: number,
    filters: SddProgressReportFilters = {},
    page = 1,
    pageSize = 25
): Promise<SddProgressReportPage> {
    const params = sddProgressReportParams(projectId, filters);
    params.set("page", String(page));
    params.set("page_size", String(pageSize));
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproducts/sdd-progress/report?${params}`, idToken, { method: "GET" });
    if (!response.ok) {
        throw new Error(`Fetching SDD progress report failed: ${response.statusText}`);
    }
    return await response.json();
}

export async function exportSddProgressReport(
    idToken: string,
    projectId: number,
    filters: SddProgressReportFilters = {}
): Promise<{ blob: Blob; filename: string }> {
    const params = sddProgressReportParams(projectId, filters);
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproducts/sdd-progress/export?${params}`, idToken, { method: "GET" });
    if (!response.ok) {
        throw new Error(`Exporting SDD progress report failed: ${response.statusText}`);
    }
    const disposition = response.headers.get("content-disposition") || "";
    const filename = disposition.match(/filename="?([^";]+)"?/i)?.[1] || "sdd-work-product-progress.xlsx";
    return { blob: await response.blob(), filename };
}

export async function getUserVectorFiles(idToken: string, userid: number): Promise<VectorFile[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/get_all_user_files/${userid}`, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        throw new Error(`Listing vector files failed: ${response.statusText}`);
    }
    const dataResponse = await response.json();
    return dataResponse.files;
}

//TODO: Wire up for real not just mock
export async function saveUserVectorFile(fileName: string, idToken: string): Promise<boolean> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/vector_files/save_to_database`, idToken, {
        method: "POST",
        body: JSON.stringify({ filename: fileName, library: false, text: "Mock File Save" })
    });
    if (!response.ok) {
        throw new Error(`Listing vector files failed: ${response.statusText}`);
    }
    const dataResponse = await response.json();
    return dataResponse.files;
}

export async function updateUserAnswer(answerId: number, answer: string, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/answers/${answerId}`, idToken, {
        method: "PUT",
        body: JSON.stringify({ answer })
    });

    if (!response.ok) {
        throw new Error(`Updating answer failed: ${response.statusText}`);
    }

    return await response.json();
}

// api.ts
export async function addTermsAcceptanceLog(userMetadata: Record<string, any>, idToken: string, userId: number) {
    const r = await fetch(`${BACKEND_URI}/api/user/terms-acceptance`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`
        },
        body: JSON.stringify({ user_metadata: userMetadata })
    });
    if (!r.ok) throw new Error(`Failed to record terms acceptance: ${r.status}`);
}

// Small helper so requests don't hang forever
function withTimeout(ms: number) {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), ms);
    return { controller, clear: () => clearTimeout(id) };
}

/**
 * Fetch the Terms & Conditions HTML from your backend proxy.
 * Backend route: GET /api/terms  -> returns text/html
 */
export async function fetchTermsHtml(): Promise<string> {
    const { controller, clear } = withTimeout(12_000);
    try {
        const res = await fetch(`${BACKEND_URI}/api/terms`, {
            method: "GET",
            headers: {
                Accept: "text/html,application/xhtml+xml"
            },
            signal: controller.signal
        });

        if (!res.ok) {
            // propagate backend error text if available
            const msg = await res.text().catch(() => "");
            throw new Error(`Failed to load Terms (${res.status} ${res.statusText})${msg ? `: ${msg}` : ""}`);
        }

        return await res.text();
    } finally {
        clear();
    }
}

export async function fetchTermsMeta(idToken: string) {
    console.log("Fetching terms meta with token:", idToken); // Debugging line
    const res = await fetchWithAuth(`${BACKEND_URI}/api/terms/meta`, idToken, { method: "GET" });
    if (!res.ok) throw new Error(`Failed to get terms meta: ${res.status}`);
    return res.json() as Promise<{
        show_terms: boolean;
        requires_acceptance: boolean;
        agreement_id: number | null;
        tc_version: string | null;
    }>;
}

export async function fetchSubscriptionStatus(idToken: string): Promise<SubscriptionStatus> {
    const res = await fetchWithAuth(`${BACKEND_URI}/api/user/subscription-status`, idToken, { method: "GET" });
    if (!res.ok) throw new Error(`Failed to get subscription status: ${res.status}`);
    return res.json() as Promise<SubscriptionStatus>;
}

// ============================================
// Stripe Billing API functions
// ============================================

export interface StripePrice {
    id: string;
    product_id: string;
    product_name: string;
    product_description: string | null;
    unit_amount: number;
    currency: string;
    interval: string | null;
    interval_count: number | null;
    lookup_key: string | null;
    metadata: Record<string, string>;
}

export interface StripePricesResponse {
    prices: StripePrice[];
    user_email?: string;
    success: boolean;
}

export interface StripeCheckoutResponse {
    url: string;
    session_id: string;
    success: boolean;
}

export async function fetchStripePrices(idToken: string): Promise<StripePricesResponse> {
    const res = await fetchWithAuth(`${BACKEND_URI}/api/billing/prices`, idToken, { method: "GET" });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || `Failed to get prices: ${res.status}`);
    }
    return res.json() as Promise<StripePricesResponse>;
}

export async function createStripeCheckoutSession(
    idToken: string,
    priceIds: string | string[],
    successUrl?: string,
    cancelUrl?: string,
    attributionData?: {
        ga_session_id?: string | null;
        gclid?: string | null;
        utm_source?: string | null;
        utm_medium?: string | null;
        utm_campaign?: string | null;
    },
    clientMetadata?: any
): Promise<StripeCheckoutResponse> {
    // Support both single price ID (string) and multiple price IDs (array)
    const body: Record<string, unknown> = {
        success_url: successUrl,
        cancel_url: cancelUrl
    };

    if (Array.isArray(priceIds)) {
        body.price_ids = priceIds;
    } else {
        body.price_id = priceIds;
    }

    // Add attribution data if provided
    if (attributionData) {
        if (attributionData.ga_session_id) body.ga_session_id = attributionData.ga_session_id;
        if (attributionData.gclid) body.gclid = attributionData.gclid;
        if (attributionData.utm_source) body.utm_source = attributionData.utm_source;
        if (attributionData.utm_medium) body.utm_medium = attributionData.utm_medium;
        if (attributionData.utm_campaign) body.utm_campaign = attributionData.utm_campaign;
    }

    // Add comprehensive client metadata for GA4 tracking
    if (clientMetadata) {
        // Geographic data
        if (clientMetadata.ip_address) body.ip_address = clientMetadata.ip_address;
        if (clientMetadata.geo?.country) body.country = clientMetadata.geo.country;
        if (clientMetadata.geo?.region) body.region = clientMetadata.geo.region;
        if (clientMetadata.geo?.city) body.city = clientMetadata.geo.city;
        if (clientMetadata.geo?.timezone) body.timezone = clientMetadata.geo.timezone;

        // Browser/Device data
        if (clientMetadata.browser?.userAgent) body.user_agent = clientMetadata.browser.userAgent;
        if (clientMetadata.browser?.languages) {
            body.languages = Array.isArray(clientMetadata.browser.languages) ? clientMetadata.browser.languages.join(",") : clientMetadata.browser.languages;
        }
        if (clientMetadata.browser?.clientHints?.uaPlatform) body.platform = clientMetadata.browser.clientHints.uaPlatform;
        if (clientMetadata.browser?.clientHints?.isMobile !== undefined) {
            body.is_mobile = String(clientMetadata.browser.clientHints.isMobile);
        }

        // Screen data
        if (clientMetadata.browser?.screen?.width) body.screen_width = String(clientMetadata.browser.screen.width);
        if (clientMetadata.browser?.screen?.height) body.screen_height = String(clientMetadata.browser.screen.height);
        if (clientMetadata.browser?.screen?.pixelDepth) body.pixel_depth = String(clientMetadata.browser.screen.pixelDepth);

        // Page context
        if (clientMetadata.currentUrl) body.currentUrl = clientMetadata.currentUrl;
        if (clientMetadata.pageTitle) body.pageTitle = clientMetadata.pageTitle;
        if (clientMetadata.referrer) body.referrer = clientMetadata.referrer;
    }

    const res = await fetchWithAuth(`${BACKEND_URI}/api/billing/create-checkout-session`, idToken, {
        method: "POST",
        body: JSON.stringify(body)
    });
    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || `Failed to create checkout session: ${res.status}`);
    }
    return res.json() as Promise<StripeCheckoutResponse>;
}

// User Stories API functions
export async function fetchFeatureUserStoriesApi(featureId: number, idToken: string): Promise<any[]> {
    const response = await fetch(`${BACKEND_URI}/api/features/${featureId}/user-stories`, {
        method: "GET",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { user_stories } = await response.json();
    return user_stories || [];
}

export async function deleteUserStoryApi(userStoryId: string, idToken: string): Promise<void> {
    const response = await fetch(`${BACKEND_URI}/api/user-stories/${userStoryId}`, {
        method: "DELETE",
        headers: {
            Authorization: `Bearer ${idToken}`
        }
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }
}

// ============================================
// Git Repository API functions
// ============================================

// Repository endpoints
export async function createGitRepositoryApi(repoData: Record<string, any>, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories`, idToken, {
        method: "POST",
        body: JSON.stringify(repoData)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { repository } = await response.json();
    return repository;
}

export async function getGitRepositoryApi(repoId: number, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { repository } = await response.json();
    return repository;
}

export async function getProjectGitRepositoriesApi(projectId: number, idToken: string): Promise<any[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/projects/${projectId}/git/repositories`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { repositories } = await response.json();
    return repositories;
}

export async function getProjectRepositoryIdApi(projectId: number, idToken: string): Promise<number | null> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/projects/${projectId}/git/repository-id`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        // If 404, no repository found - return null
        if (response.status === 404) {
            return null;
        }
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { repository_id } = await response.json();
    return repository_id;
}

export async function getFeatureGitRepositoriesApi(featureId: number, idToken: string): Promise<any[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/features/${featureId}/git/repositories`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { repositories } = await response.json();
    return repositories;
}

export async function updateGitRepositoryApi(repoId: number, repoData: Record<string, any>, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}`, idToken, {
        method: "PUT",
        body: JSON.stringify(repoData)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { repository } = await response.json();
    return repository;
}

export async function deleteGitRepositoryApi(repoId: number, idToken: string): Promise<void> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}`, idToken, {
        method: "DELETE"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }
}

export async function getGitRepositoryStatsApi(repoId: number, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}/stats`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    return await response.json();
}

// Commit endpoints
export async function createGitCommitApi(repoId: number, commitData: Record<string, any>, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}/commits`, idToken, {
        method: "POST",
        body: JSON.stringify(commitData)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { commit } = await response.json();
    return commit;
}

export async function getGitCommitsApi(repoId: number, idToken: string, limit: number = 100): Promise<any[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}/commits?limit=${limit}`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { commits } = await response.json();
    return commits;
}

export async function getGitCommitByHashApi(repoId: number, hash: string, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}/commits/${hash}`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { commit } = await response.json();
    return commit;
}

// File endpoints
export async function createGitFileApi(repoId: number, fileData: Record<string, any>, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}/files`, idToken, {
        method: "POST",
        body: JSON.stringify(fileData)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { file } = await response.json();
    return file;
}

export async function getGitFilesApi(repoId: number, idToken: string, language?: string): Promise<any[]> {
    const url = language ? `${BACKEND_URI}/api/git/repositories/${repoId}/files?language=${language}` : `${BACKEND_URI}/api/git/repositories/${repoId}/files`;

    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { files } = await response.json();
    return files;
}

// File content endpoints
export async function createGitFileContentApi(fileId: number, contentData: Record<string, any>, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/files/${fileId}/content`, idToken, {
        method: "POST",
        body: JSON.stringify(contentData)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { file_content } = await response.json();
    return file_content;
}

export async function getGitFileContentApi(fileId: number, idToken: string, commitId?: number): Promise<any> {
    const url = commitId ? `${BACKEND_URI}/api/git/files/${fileId}/content?commit_id=${commitId}` : `${BACKEND_URI}/api/git/files/${fileId}/content`;

    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { file_content } = await response.json();
    return file_content;
}

// Directory endpoints
export async function createGitDirectoryApi(repoId: number, directoryData: Record<string, any>, idToken: string): Promise<any> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}/directories`, idToken, {
        method: "POST",
        body: JSON.stringify(directoryData)
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { directory } = await response.json();
    return directory;
}

export async function getGitDirectoriesApi(repoId: number, idToken: string): Promise<any[]> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/git/repositories/${repoId}/directories`, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { directories } = await response.json();
    return directories;
}

export async function getGitDirectoryTreeApi(repoId: number, idToken: string, parentId?: number): Promise<any[]> {
    const url = parentId
        ? `${BACKEND_URI}/api/git/repositories/${repoId}/directory-tree?parent_id=${parentId}`
        : `${BACKEND_URI}/api/git/repositories/${repoId}/directory-tree`;

    const response = await fetchWithAuth(url, idToken, {
        method: "GET"
    });

    if (!response.ok) {
        const errorResponse = await response.json();
        const errorMessage = errorResponse?.error || response.statusText;
        throw new Error(errorMessage);
    }

    const { directories } = await response.json();
    return directories;
}

export async function getWorkProductionSectionById(sectionid: number, idToken: string): Promise<WorkProductSectionpiResponse> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/workproduct/sections/id/${sectionid}`, idToken, {
        method: "GET"
    });
    if (!response.ok) {
        throw new Error(`Getting work production section: ${response.statusText}`);
    }
    const dataResponse: WorkProductSectionpiResponse = await response.json();
    return dataResponse;
}

// ============================================
// Specification Change Analysis API functions
// ============================================

export interface QuickCompareRequest {
    projectid: number;
    userid: number;
    original_answers?: any[];
    new_answers?: any[];
    original_description?: string;
    is_new_feature?: boolean;
    is_new_user_story?: boolean;
    feature_id?: number;
    user_story_id?: string;
}

export interface QuickCompareResponse {
    job_id: string;
    message?: string;
}

export interface CompareStatusResponse {
    status: "QUEUED" | "PROCESSING" | "COMPLETED" | "FAILED";
    phase?: string;
    step?: number;
    total_steps?: number;
    message?: string;
    elapsed_seconds?: number;
    comparison_result?: any;
    error?: string;
}

export interface ApplySpecificationChangesRequest {
    projectid: number;
    userid: number;
    action: "CREATE_NEW_PRODUCT" | "UPDATE_PRODUCT" | "CANCEL_UPDATE";
    comparison_result?: any;
    original_answers?: any[];
    new_project_name?: string;
    comparison_id?: string;
}

export interface ApplySpecificationChangesResponse {
    status: string;
    message?: string;
    result?: any;
}

export interface ImportFeaturesRequest {
    projectid: number;
    sectionid: number[];
}

export interface ImportFeaturesResponse {
    features?: any[];
    user_stories?: any[];
    message?: string;
}

/**
 * Start a quick comparison analysis of specification changes
 * Uses Work Processor API for async job processing
 */
export async function startQuickCompare(request: QuickCompareRequest, idToken: string): Promise<QuickCompareResponse> {
    const quickCompareUrl = getWorkProcessorUrl("/api/quick-compare");
    const response = await fetchWithAuth(quickCompareUrl, idToken, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        throw new Error(`Quick compare failed to start: ${response.statusText}`);
    }

    return await response.json();
}

/**
 * Poll the status of a quick comparison job
 * Uses Work Processor API
 */
export async function getCompareStatus(jobId: string, idToken: string): Promise<CompareStatusResponse> {
    const statusUrl = getWorkProcessorUrl(`/api/compare-specifications/${jobId}/status`);
    const response = await fetchWithAuth(statusUrl, idToken, { method: "GET" });

    if (!response.ok) {
        throw new Error(`Failed to get comparison status: ${response.statusText}`);
    }

    return await response.json();
}

/**
 * Apply specification changes to work products
 * Uses Work Processor API
 */
export async function applySpecificationChanges(request: ApplySpecificationChangesRequest, idToken: string): Promise<ApplySpecificationChangesResponse> {
    const applyUrl = getWorkProcessorUrl("/api/apply-specification-changes");
    const response = await fetchWithAuth(applyUrl, idToken, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        throw new Error(`Failed to apply specification changes: ${response.statusText}`);
    }

    return await response.json();
}

/**
 * Import features and user stories from preprocessed workproduct content
 * Uses main backend API
 */
export async function importFeaturesFromPreprocessing(request: ImportFeaturesRequest, idToken: string): Promise<ImportFeaturesResponse> {
    const response = await fetchWithAuth(`${BACKEND_URI}/api/features/from-pre-processing`, idToken, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(request)
    });

    if (!response.ok) {
        throw new Error(`Failed to import features from preprocessing: ${response.statusText}`);
    }

    return await response.json();
}
